import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, Send, Loader2, CheckCircle, ArrowRight, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VerifyEmailInterstitial() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    try {
      setSending(true);
      const res = await axios.post(`${API}/auth/resend-verification`);
      if (res.data.already_verified) {
        toast.success('Votre email est déjà vérifié !');
        if (refreshUser) await refreshUser();
        navigate('/accueil', { replace: true });
      } else if (res.data.sent) {
        setSent(true);
        toast.success('Email envoyé !');
      } else {
        toast.error(res.data.message || "Impossible d'envoyer l'email pour le moment.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleContinue = () => {
    // Mark that user dismissed the interstitial for this session
    sessionStorage.setItem('nannyou_email_skip', '1');
    navigate('/accueil', { replace: true });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ backgroundColor: 'var(--bg-secondary)' }}
      data-testid="verify-email-interstitial"
    >
      <Card className="w-full max-w-md border-0 shadow-xl" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <CardContent className="pt-10 pb-8 px-8 text-center">
          {/* Icon */}
          <div
            className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: 'var(--primary-50)' }}
          >
            <Mail className="h-10 w-10" style={{ color: 'var(--primary-400)' }} />
          </div>

          {/* Title */}
          <h1
            className="text-2xl font-bold mb-2"
            style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-heading)' }}
            data-testid="verify-email-title"
          >
            Vérifiez votre email
          </h1>

          {/* Subtitle */}
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
            Pour sécuriser votre compte et accéder à toutes les fonctionnalités,
            veuillez confirmer votre adresse email.
          </p>

          {/* Email display */}
          <div
            className="rounded-lg px-4 py-3 mb-6 flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--neutral-50)', border: '1px solid var(--neutral-200)' }}
          >
            <Mail className="h-4 w-4 shrink-0" style={{ color: 'var(--text-muted)' }} />
            <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }} data-testid="verify-email-address">
              {user?.email}
            </span>
          </div>

          {/* Status message after sent */}
          {sent && (
            <div
              className="rounded-lg px-4 py-3 mb-6 flex items-start gap-2"
              style={{ backgroundColor: 'var(--success-light)', border: '1px solid var(--success)' }}
              data-testid="verify-email-sent-msg"
            >
              <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--success)' }} />
              <p className="text-sm text-left" style={{ color: 'var(--success-dark)' }}>
                Un email de vérification a été envoyé. Vérifiez votre boîte de réception
                (et les spams). Le lien est valide <strong>48 heures</strong>.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <Button
              className="w-full h-12 text-base font-semibold gap-2 text-white"
              style={{ backgroundColor: 'var(--primary-400)' }}
              onClick={handleResend}
              disabled={sending}
              data-testid="send-verification-btn"
            >
              {sending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Send className="h-4 w-4" />
              }
              {sent ? 'Renvoyer un nouveau lien' : 'Envoyer le lien de vérification'}
            </Button>

            <Button
              variant="ghost"
              className="w-full h-10 text-sm gap-2"
              style={{ color: 'var(--text-secondary)' }}
              onClick={handleContinue}
              data-testid="skip-verification-btn"
            >
              Continuer sans vérifier
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Info footer */}
          <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--neutral-100)' }}>
            <div className="flex items-start gap-2 text-left">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--primary-400)' }} />
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                La vérification de votre email est requise pour créer des contrats
                et effectuer des déclarations Pajemploi.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
